import View from '@play-co/timestep-core/lib/ui/View';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import { ButtonView, ImageScaleView, animate } from '@play-co/timestep-core/ui';
import { AssetGroup } from '@play-co/timestep-core';
import { Feature } from 'src/replicant/analytics';
import { waitForItPromise } from 'src/lib/utils';
import { downscale } from 'src/conf/scale';
import StateObserver from 'src/StateObserver';
import sounds from 'src/lib/sounds';

type CloseHandler = () => void;

export default class PopupBuddiesInfo extends PopupBasicLazy {
  constructor(opts: { superview: View; close: CloseHandler }) {
    const width = downscale(1080);
    const height = downscale(1920);

    super({
      superview: opts.superview,
      skipBackground: true,
      skipTitle: true,
      width,
      height,
      close: () => opts.close(),
      tracking: {
        feature: Feature.BUDDIES._,
        $subFeature: Feature.BUDDIES.POPUP_INFO,
      },
      zIndex: 10000,
    });

    this.buttonClose.hide();

    // This color is the same as backgroundDarker in uiConfig.popups, but less transparent
    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.9)',
    });

    // Tap to continue
    new ButtonView({
      superview: this.box,
      width: width,
      height: height,
      zIndex: 1,
      onClick: () => {
        sounds.sfx.play('ok');
        opts.close();
      },
    });
  }

  public getAssetsGroup() {
    return new AssetGroup([
      ...AssetGroup.constructURLs([
        'assets/ui/buddies/common',
        'assets/ui/buddies/popupInfo',
      ]),
    ]);
  }

  public async init() {
    super.init({});
  }

  public async fadeIn() {
    await super.fadeIn();
    this.animateIn();
  }

  private async animateIn() {
    await waitForItPromise(300);

    sounds.sfx.play('win');

    const locale = StateObserver.getLocale();
    const en = locale === 'en';
    const cx = this.box.style.width * 0.5;

    // Title
    const title = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/buddies/common/title_${locale}.png`,
      width: downscale(en ? 500 : 494),
      height: downscale(en ? 233 : 258),
      scaleMethod: 'contain',
      x: cx,
      y: 100,
      centerOnOrigin: true,
    });
    await this.showAnimated(title);

    // How to Play
    const howToPlay = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/buddies/popupInfo/how_to_play_${locale}.png`,
      width: downscale(en ? 288 : 147),
      height: downscale(en ? 61 : 57),
      scaleMethod: 'contain',
      x: cx,
      y: 205,
      centerOnOrigin: true,
    });
    await this.showAnimated(howToPlay);

    // Item 1 + Text 1 (left)
    const item1 = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/buddies/popupInfo/item_1.png',
      width: downscale(405),
      height: downscale(283),
      scaleMethod: 'contain',
      x: 180,
      y: 300,
      centerOnOrigin: true,
    });
    new ImageScaleView({
      superview: item1,
      image: `assets/ui/buddies/popupInfo/text_1_${locale}.png`,
      width: downscale(552),
      height: downscale(131),
      scaleMethod: 'contain',
      x: item1.style.width * 0.5,
      y: item1.style.height * 0.5 + 120,
      centerOnOrigin: true,
    });
    await this.showAnimated(item1);

    // Arrow 1
    const arrow1 = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/buddies/popupInfo/arrow.png',
      width: downscale(129),
      height: downscale(129),
      scaleMethod: 'contain',
      x: cx + 20,
      y: 390,
      centerOnOrigin: true,
    });
    await this.showAnimated(arrow1);

    // Item 2 + Text 2 (right)
    const item2 = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/buddies/popupInfo/item_2.png',
      width: downscale(350),
      height: downscale(331),
      scaleMethod: 'contain',
      x: 530,
      y: 400,
      centerOnOrigin: true,
    });
    new ImageScaleView({
      superview: item2,
      image: `assets/ui/buddies/popupInfo/text_2_${locale}.png`,
      width: downscale(552),
      height: downscale(270),
      scaleMethod: 'contain',
      x: item2.style.width * 0.5,
      y: item2.style.height * 0.5 + 200,
      centerOnOrigin: true,
    });
    await this.showAnimated(item2);

    // Arrow 2 (flipped)
    const arrow2 = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/buddies/popupInfo/arrow.png',
      width: downscale(129),
      height: downscale(129),
      scaleMethod: 'contain',
      flipX: true,
      x: cx - 30,
      y: 600,
      centerOnOrigin: true,
    });
    await this.showAnimated(arrow2);

    // Item 3 (left) + Text 3 (right)
    const item3 = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/buddies/popupInfo/item_3.png',
      width: downscale(394),
      height: downscale(272),
      scaleMethod: 'contain',
      x: 170,
      y: 610,
      centerOnOrigin: true,
    });
    new ImageScaleView({
      superview: item3,
      image: `assets/ui/buddies/popupInfo/text_3_${locale}.png`,
      width: downscale(459),
      height: downscale(131),
      scaleMethod: 'contain',
      x: item3.style.width * 0.5,
      y: item3.style.height * 0.5 + 110,
      centerOnOrigin: true,
    });
    await this.showAnimated(item3);

    // Arrow 3
    const arrow3 = new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/buddies/popupInfo/arrow.png',
      width: downscale(129),
      height: downscale(129),
      scaleMethod: 'contain',
      x: cx + 10,
      y: 740,
      centerOnOrigin: true,
    });
    await this.showAnimated(arrow3);

    // Multiplier (self-contained text + image)
    const multiplier = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/buddies/popupInfo/multipliers_${locale}.png`,
      width: downscale(929),
      height: downscale(635),
      scaleMethod: 'contain',
      x: cx,
      y: 940,
      centerOnOrigin: true,
    });
    await this.showAnimated(multiplier);

    // Tap to Continue
    const tapToContinue = new ImageScaleView({
      superview: this.box,
      image: `assets/ui/buddies/popupInfo/tap_to_continue_${locale}.png`,
      width: downscale(en ? 569 : 433),
      height: downscale(en ? 87 : 84),
      scaleMethod: 'contain',
      x: cx,
      y: 1210,
      centerOnOrigin: true,
    });
    await this.showAnimated(tapToContinue);
  }

  private async showAnimated(view: View) {
    view.updateOpts({ scale: 0 });
    animate(view).then({ scale: 1 }, 300, animate.easeOutBack);
    sounds.sfx.play('puzzle-ice-tap');
    await waitForItPromise(200);
  }
}
